import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import { getImageSrc } from '../../utils/productStorefront';
import {
  formatCartDate,
  formatCartMoney,
  parseCartDetailResponse,
} from './cartUtils';
import '../css/Category.css';
import '../css/cartAdmin.css';

const CartDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!id) return;

    let active = true;
    setLoading(true);
    setError(null);

    apiFetch(`/Cart/Admin/GetDetail/${id}`)
      .then(({ data }) => {
        if (!active) return;
        const parsed = parseCartDetailResponse(data);
        if (!parsed) {
          setError('Cart not found.');
          setDetail(null);
        } else {
          setDetail(parsed);
        }
      })
      .catch((e) => {
        if (!active) return;
        setError(e.message || 'Could not load cart.');
        setDetail(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  const items = detail?.items ?? [];
  const grandTotal = detail?.totalAmount ?? 0;

  return (
    <AdminLayout>
      <div className="cart-detail-page">
        <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
          <AdminLink to="/admin">Dashboard</AdminLink>
          <span style={{ margin: '0 6px' }}>/</span>
          <AdminLink to="/admin/cart">Carts</AdminLink>
          <span style={{ margin: '0 6px' }}>/</span>
          <span>Cart #{id}</span>
        </p>

        <div className="row">
          <div className="col-lg-12 grid-margin stretch-card">
            <div className="card" style={{ border: '1px solid #e8ecf1', borderRadius: '14px', boxShadow: '0 2px 12px rgba(15,23,42,0.04)' }}>
              <div className="card-body" style={{ padding: '1.75rem' }}>
                <div className="cart-detail-header">
                  <h4 className="card-title">Cart Details #{id}</h4>
                  <button type="button" className="cart-detail-back-btn" onClick={() => navigate('/admin/cart')}>
                    ← Back to Carts
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger py-2 mb-0" style={{ fontSize: '0.875rem' }}>{error}</div>
                ) : detail ? (
                  <>
                    <div className="cart-detail-meta">
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">Username</span>
                        <span className="cart-detail-meta-value">{detail.userName}</span>
                      </div>
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">Status</span>
                        <span className="cart-detail-meta-value">
                          <span className="cart-detail-status">{detail.cartStatus || 'Added'}</span>
                        </span>
                      </div>
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">Created</span>
                        <span className="cart-detail-meta-value">{formatCartDate(detail.createdAt)}</span>
                      </div>
                      <div className="cart-detail-meta-card">
                        <span className="cart-detail-meta-label">Total Qty</span>
                        <span className="cart-detail-meta-value">{detail.totalQuantity}</span>
                      </div>
                    </div>

                    {items.length === 0 ? (
                      <p className="cart-detail-empty">No items in this cart.</p>
                    ) : (
                      <>
                        <div className="cart-detail-table-wrap">
                          <table className="cart-detail-table">
                            <thead>
                              <tr>
                                <th className="cart-detail-th-image">Image</th>
                                <th>Product</th>
                                <th className="cart-detail-th-qty">Qty</th>
                                <th className="cart-detail-th-price">Price</th>
                                <th className="cart-detail-th-total">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item) => {
                                const imageUrl = item.productImage ? getImageSrc(item.productImage) : '';
                                return (
                                  <tr key={item.cartItemId ?? `${item.productId}-${item.createdAt}`}>
                                    <td className="cart-detail-td-image">
                                      {imageUrl ? (
                                        <img
                                          src={imageUrl}
                                          alt={item.productName}
                                          className="cart-detail-thumb"
                                        />
                                      ) : (
                                        <div className="cart-detail-thumb-placeholder">N/A</div>
                                      )}
                                    </td>
                                    <td>
                                      <span className="cart-detail-product-name">{item.productName}</span>
                                      <span className="cart-detail-product-id">ID: {item.productId}</span>
                                    </td>
                                    <td className="cart-detail-td-qty">{item.quantity}</td>
                                    <td className="cart-detail-td-price">{formatCartMoney(item.price)}</td>
                                    <td className="cart-detail-td-total">{formatCartMoney(item.totalPrice)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div className="cart-detail-summary">
                          <div className="cart-detail-summary-box">
                            <div className="cart-detail-summary-row">
                              <span className="cart-detail-summary-label">Grand Total</span>
                              <span className="cart-detail-summary-amount">{formatCartMoney(grandTotal)}</span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CartDetailPage;
